/**
 * NotificationDetail - Right panel showing selected notification details.
 * Renders in the same style as DraftDetail in /communications/manager:
 * category icon, title, metadata row, tool chips, full markdown body via MDXEditor.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import MarkunreadIcon from '@mui/icons-material/Markunread';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import { useTheme } from '@mui/material/styles';
import { getColors } from '@mercedes-benz/odp-theme-v2';
import { useApi, fetchApiRef } from '@backstage/core-plugin-api';
import {
  MDXEditor,
  headingsPlugin,
  listsPlugin,
  quotePlugin,
  thematicBreakPlugin,
  linkPlugin,
  tablePlugin,
  imagePlugin,
} from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css';
import { extractHashtags } from '../../../utils/labelParser';
import Link from '@mui/material/Link';
import LaunchIcon from '@mui/icons-material/Launch';
import { formatDate, parseNotificationContent } from './utils';
import type { NotificationDetailProps } from './types';

const CATEGORY_ICONS: Record<string, typeof NewspaperIcon> = {
  news: NewspaperIcon,
  notifications: NotificationsIcon,
  tasks: AssignmentIcon,
  incidents: AnnouncementIcon,
};

function stripHashtags(text: string): string {
  return text
    .replaceAll(/#[\w-]+/g, '')
    .replaceAll(/[ \t]{2,}/g, ' ')
    .trim();
}

function safeLinkHref(url: string | undefined): string | undefined {
  if (!url) return undefined;
  try {
    const { protocol } = new URL(url);
    return protocol === 'http:' || protocol === 'https:' ? url : undefined;
  } catch {
    return undefined;
  }
}

export function NotificationDetail({
  notification,
  onMarkAsUnread,
  onDelete,
  onLabelClick,
}: NotificationDetailProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const c = getColors(theme.palette.mode);
  const fetchApi = useApi(fetchApiRef);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const objectUrlsRef = useRef<string[]>([]);

  useEffect(() => {
    const urls = objectUrlsRef.current;
    return () => {
      urls.forEach(u => URL.revokeObjectURL(u));
    };
  }, [notification.id]);

  const payload = notification.payload as any;
  const { title, description, sentBy } = parseNotificationContent(notification);
  const labels = extractHashtags(description);
  const formattedDate = formatDate(
    notification.created ?? new Date().toISOString(),
  );
  const link = payload?.link;
  const postedBy = sentBy || notification.origin || 'System';

  const topic = payload?.topic ?? 'notifications';
  const CategoryIcon = CATEGORY_ICONS[topic] ?? NotificationsIcon;

  const viewerPlugins = useMemo(
    () => [
      headingsPlugin(),
      listsPlugin(),
      quotePlugin(),
      thematicBreakPlugin(),
      linkPlugin(),
      tablePlugin(),
      imagePlugin({
        imagePreviewHandler: async (src: string) => {
          try {
            const response = await fetchApi.fetch(src);
            if (!response.ok) return src;
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            objectUrlsRef.current.push(objectUrl);
            return objectUrl;
          } catch {
            return src;
          }
        },
      }),
    ],
    [fetchApi],
  );

  const markdownBody = stripHashtags(description);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Scrollable content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3, pb: 10 }}>
        {/* Header row: icon + title + actions */}
        <Box
          sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start', mb: 0 }}
        >
          {/* Category icon circle */}
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              backgroundColor: isDark ? '#e0e0e0' : '#1a1a1a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              color: isDark ? '#1a1a1a' : '#ffffff',
            }}
          >
            <CategoryIcon sx={{ fontSize: 32 }} />
          </Box>

          {/* Title and meta */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                }}
              >
                <Box
                  component="h2"
                  sx={{
                    fontSize: 22,
                    fontWeight: 700,
                    color: 'text.primary',
                    lineHeight: 1.3,
                    m: 0,
                    mb: 1.5,
                    flex: 1,
                  }}
                >
                  {title}
                </Box>

                {/* Three-dot menu */}
                <IconButton
                  size="small"
                  onClick={e => setAnchorEl(e.currentTarget)}
                  aria-label="more actions"
                  sx={{ ml: 1, flexShrink: 0 }}
                >
                  <MoreHorizIcon />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={menuOpen}
                  onClose={() => setAnchorEl(null)}
                >
                  <MenuItem
                    onClick={() => {
                      onMarkAsUnread();
                      setAnchorEl(null);
                    }}
                  >
                    <MarkunreadIcon sx={{ mr: 1, fontSize: 20 }} />
                    Mark as unread
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      onDelete();
                      setAnchorEl(null);
                    }}
                    sx={{ color: 'error.main' }}
                  >
                    <VisibilityOffIcon sx={{ mr: 1, fontSize: 20 }} />
                    Hide
                  </MenuItem>
                </Menu>
              </Box>

              {/* Meta row */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  flexWrap: 'wrap',
                  fontSize: 12,
                  color: 'text.secondary',
                }}
              >
                <span>{formattedDate}</span>
                {postedBy && (
                  <span>
                    {' · '}
                    <strong>Posted by:</strong> {postedBy}
                  </span>
                )}
              </Box>

              {/* Tool chips (extracted from hashtags) */}
              {labels.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    flexWrap: 'wrap',
                    mt: 1,
                  }}
                >
                  {labels.map((label: string) => (
                    <Box
                      component="span"
                      key={label}
                      onClick={
                        onLabelClick ? () => onLabelClick(label) : undefined
                      }
                      sx={{
                        display: 'inline-block',
                        px: '10px',
                        py: '2px',
                        borderRadius: '12px',
                        fontSize: 11,
                        fontWeight: 500,
                        background: isDark
                          ? 'rgba(255,255,255,0.08)'
                          : 'rgba(0,0,0,0.06)',
                        color: 'text.secondary',
                        ...(onLabelClick && { cursor: 'pointer' }),
                      }}
                    >
                      {label}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 2.5 }} />

            {/* Markdown body */}
            <Box
              sx={{
                fontSize: '0.9rem',
                lineHeight: 1.75,
                color: 'text.primary',
                '& .mdxeditor': {
                  '--baseText': c.text.primary,
                  '--baseTextContrast': c.text.primary,
                  '--basePageBg': 'transparent',
                  backgroundColor: 'transparent',
                  color: c.text.primary,
                  fontSize: '0.9rem',
                  lineHeight: 1.75,
                },
                '& .mdxeditor p': { mt: 0, mb: '14px', color: c.text.primary },
                '& .mdxeditor a': {
                  color: c.text.link,
                  textDecoration: 'underline',
                },
                '& .mdxeditor code': {
                  backgroundColor: c.background.elevated,
                  px: '0.4em',
                  py: '0.15em',
                  borderRadius: '3px',
                  fontFamily: 'monospace',
                  fontSize: '0.85em',
                },
                '& .mdxeditor pre': {
                  backgroundColor: c.background.default,
                  color: c.text.primary,
                  p: 2,
                  borderRadius: '6px',
                  overflow: 'auto',
                  mb: '14px',
                },
                '& .mdxeditor ul, & .mdxeditor ol': { pl: '24px', mb: '14px' },
                '& .mdxeditor li': { mb: '4px' },
                '& .mdxeditor h1': {
                  fontSize: '1.4em',
                  fontWeight: 700,
                  mt: '28px',
                  mb: '12px',
                },
                '& .mdxeditor h2': {
                  fontSize: '1.2em',
                  fontWeight: 700,
                  mt: '24px',
                  mb: '10px',
                },
                '& .mdxeditor h3': {
                  fontSize: '1.05em',
                  fontWeight: 600,
                  mt: '20px',
                  mb: '8px',
                },
                '& .mdxeditor blockquote': {
                  borderLeft: `3px solid ${theme.palette.primary.main}`,
                  pl: '16px',
                  ml: 0,
                  fontStyle: 'italic',
                  color: 'text.secondary',
                },
                '& .mdxeditor hr': {
                  border: 'none',
                  borderTop: `1px solid ${theme.palette.divider}`,
                  my: '20px',
                },
                '& .mdxeditor img': {
                  maxWidth: '100%',
                  height: 'auto',
                },
              }}
            >
              <MDXEditor
                markdown={markdownBody}
                readOnly
                plugins={viewerPlugins}
              />
            </Box>

            <Divider sx={{ my: 2.5 }} />

            {/* Footer: link */}
            {link && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 1,
                }}
              >
                {(() => {
                  const safeHref = safeLinkHref(link);
                  return safeHref ? (
                    <Link
                      href={safeHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="always"
                      title={safeHref}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        maxWidth: 400,
                      }}
                    >
                      <span
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {safeHref}
                      </span>
                      <LaunchIcon
                        sx={{ ml: 0.5, flexShrink: 0 }}
                        fontSize="small"
                      />
                    </Link>
                  ) : null;
                })()}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
